import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useMatch, useNavigate, useParams } from "react-router-dom";

import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  getFreightOrderStatusMasterEdit,
  storeFreightOrderStatusMaster,
  updateFreightOrderStatusMaster
} from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

function colorPickerValue(hex) {
  const t = String(hex || "").trim();
  if (/^#[0-9A-Fa-f]{6}$/i.test(t)) return t.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/i.test(t)) {
    const r = t[1];
    const g = t[2];
    const b = t[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return "#000000";
}

const FreightOrderStatusFormPage = () => {
  const navigate = useNavigate();
  const newMatch = useMatch({ path: "/freight/order_status/new", end: true });
  const { id: routeId } = useParams();
  const isNew = Boolean(newMatch);
  const statusId = isNew ? null : routeId;

  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Order status", link: "/freight/order_status" },
      { label: isNew ? "New order status" : `Edit #${statusId ?? ""}` }
    ],
    [roleLabel, isNew, statusId]
  );

  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [subStatus, setSubStatus] = useState("");
  const [textColor, setTextColor] = useState("#ffffff");
  const [backgroundColor, setBackgroundColor] = useState("#0d6efd");
  const [orderNo, setOrderNo] = useState("");
  const [status, setStatus] = useState("1");

  const loadEdit = useCallback(async () => {
    if (!statusId) return;
    setLoading(true);
    try {
      const res = await getFreightOrderStatusMasterEdit(statusId);
      const d = res?.data?.freightOrderStatusData;
      if (!d) throw new Error("Not found");
      setName(String(d.name ?? ""));
      setSubStatus(String(d.sub_status ?? ""));
      setTextColor(String(d.text_color ?? "#ffffff").trim() || "#ffffff");
      setBackgroundColor(String(d.background_color ?? "#0d6efd").trim() || "#0d6efd");
      setOrderNo(d.order_no != null ? String(d.order_no) : "");
      setStatus(String(d.status ?? "1"));
    } catch (e) {
      showError(e.message || "Load failed");
      navigate("/freight/order_status", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate, statusId]);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    loadEdit();
  }, [isNew, loadEdit]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      showError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      if (isNew) {
        await storeFreightOrderStatusMaster({
          name: trimmed,
          sub_status: subStatus.trim() || undefined,
          text_color: textColor.trim() || undefined,
          background_color: backgroundColor.trim() || undefined,
          order_no: orderNo === "" ? undefined : Number(orderNo),
          status
        });
        showSuccess("Order status created");
      } else {
        await updateFreightOrderStatusMaster({
          id: Number(statusId),
          name: trimmed,
          sub_status: subStatus.trim() || undefined,
          text_color: textColor.trim() || undefined,
          background_color: backgroundColor.trim() || undefined,
          order_no: orderNo === "" ? undefined : Number(orderNo),
          status
        });
        showSuccess("Order status updated");
      }
      navigate("/freight/order_status");
    } catch (err) {
      showError(err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout breadcrumbs={breadcrumbs}>
        <div className="max-w-[720px] card offers-table p-6">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[720px]">
        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="m-0">{isNew ? "New order status" : "Edit order status"}</h3>
            <Link to="/freight/order_status" className="btn-outline no-underline text-sm">
              ← Back to list
            </Link>
          </div>

          <form className="freight-admin-form space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required maxLength={255} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sub status</label>
              <input
                type="text"
                value={subStatus}
                onChange={(e) => setSubStatus(e.target.value)}
                placeholder="Optional subtitle"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text color</label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="color"
                    value={colorPickerValue(textColor)}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded border border-gray-200 p-0.5"
                    aria-label="Text color"
                  />
                  <input type="text" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="flex-1 min-w-[120px]" placeholder="#ffffff" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background color</label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="color"
                    value={colorPickerValue(backgroundColor)}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded border border-gray-200 p-0.5"
                    aria-label="Background color"
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="flex-1 min-w-[120px]"
                    placeholder="#0d6efd"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display order</label>
              <input type="number" min={0} value={orderNo} onChange={(e) => setOrderNo(e.target.value)} placeholder="Optional" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
              <span className="text-gray-600">Preview:</span>{" "}
              <span
                className="ml-2 inline-flex flex-col rounded-md border border-gray-200 px-2 py-1 font-medium"
                style={{ color: textColor, backgroundColor: backgroundColor }}
              >
                <span>{name.trim() || "Name"}</span>
                {subStatus.trim() ? <span className="text-xs opacity-90">{subStatus.trim()}</span> : null}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving…" : isNew ? "Create" : "Save changes"}
              </button>
              <Link to="/freight/order_status" className="btn-outline no-underline inline-flex items-center">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightOrderStatusFormPage;
